import React, { useState, useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import EditIcon from '@mui/icons-material/Edit'
import { createNewUser, updateUser } from './Functions'
import { invalidateQuery, FC } from '../../Services'

const USER_ROLES = [
  { label: 'Super Admin', value: 'siapAdmin' },
  { label: 'Developer SIAP', value: 'siapDeveloper' },
  { label: 'Amministratore di Agenzia', value: 'agencyAdmin' }
]

const emptyForm = { name: '', lastName: '', email: '', password: '', role: '', allAgencies: [] }

export const UserDialog = ({ open, onClose, user, agencies }) => {
  const isEdit = !!user
  const [form, setForm] = useState(emptyForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (open) {
      if (user) {
        setForm({
          name: user.name || '',
          lastName: user.lastName || '',
          email: user.email || '',
          password: '',
          role: user.role || '',
          allAgencies: user.allAgencies || []
        })
      } else {
        setForm(emptyForm)
      }
    }
  }, [open, user])

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleRoleChange = (e) => {
    const newRole = e.target.value
    setForm((prev) => ({
      ...prev,
      role: newRole,
      allAgencies: newRole === 'siapAdmin' ? [] : prev.allAgencies
    }))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const service = FC.service('users')

    const userData = {
      name: form.name.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      password: form.password,
      role: form.role,
      allAgencies: form.role === 'agencyAdmin' ? form.allAgencies : undefined
    }

    let success = false
    if (isEdit) {
      success = await updateUser(user._id, userData, service)
    } else {
      success = await createNewUser(userData, service)
    }

    setIsSubmitting(false)
    if (success) {
      invalidateQuery('users')
      onClose()
    }
  }

  const Icon = isEdit ? EditIcon : PersonAddIcon

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='user-dialog-title'
    >
      <DialogTitle
        id='user-dialog-title'
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Icon color='primary' />
          <Typography variant='h6' component='span' fontWeight={600}>
            {isEdit ? 'Modifica Utente' : 'Nuovo Utente'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size='small' aria-label='Chiudi'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction='row' spacing={2}>
            <TextField
              label='Nome'
              value={form.name}
              onChange={handleChange('name')}
              fullWidth
              required
              size='small'
              autoFocus
            />
            <TextField
              label='Cognome'
              value={form.lastName}
              onChange={handleChange('lastName')}
              fullWidth
              required
              size='small'
            />
          </Stack>

          <TextField
            label='Email'
            value={form.email}
            onChange={handleChange('email')}
            type='email'
            fullWidth
            required
            size='small'
          />

          <TextField
            label={isEdit ? 'Nuova Password (opzionale)' : 'Password'}
            value={form.password}
            onChange={handleChange('password')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            required={!isEdit}
            size='small'
            helperText={isEdit ? 'Lascia vuoto per non modificare la password' : ''}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge='end'
                      aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                      size='small'
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
          />

          <FormControl fullWidth required size='small'>
            <InputLabel id='role-select-label'>Ruolo</InputLabel>
            <Select
              labelId='role-select-label'
              value={form.role}
              onChange={handleRoleChange}
              label='Ruolo'
            >
              {USER_ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.role === 'agencyAdmin' && (
            <Autocomplete
              multiple
              options={agencies || []}
              getOptionLabel={(option) => option?.name || ''}
              value={form.allAgencies}
              onChange={(_, newValue) => setForm((prev) => ({ ...prev, allAgencies: newValue }))}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option._id}
                    label={option.name}
                    size='small'
                    variant='outlined'
                  />
                ))
              }
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Stack>
                    <Typography variant='body2' fontWeight={500}>{option.name}</Typography>
                    {option.opUnit && option.opUnit !== 'none' && (
                      <Typography variant='caption' color='text.secondary'>
                        Op. Unit: {option.opUnit}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label='Agenzie' required size='small' />
              )}
            />
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          size='small'
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          Annulla
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          size='small'
          disabled={isSubmitting}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isSubmitting
            ? 'Salvataggio...'
            : isEdit ? 'Salva Modifiche' : 'Crea Utente'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
